import { useCallback, useEffect, useRef, useState } from "react";
import { formatUnits, isAddress } from "viem";

import { useEvmBackend } from "../context";
import { MOCK_ERC20_ABI } from "../lib/abis";
import { TOKEN_DECIMALS } from "../lib/constants";
import { isConnectionError, parseViemError } from "../lib/errors";

import { useOnBlock } from "./useOnBlock";

export interface TokenBalance {
  raw: bigint;
  formatted: string;
  decimals: number;
}

export interface BalanceFetchError {
  token: string;
  message: string;
  isConnection: boolean;
}

/** Check if a token address is valid (not "0x" or other malformed values). */
function isValidTokenAddress(addr: string): addr is `0x${string}` {
  return isAddress(addr);
}

export function useTokenBalances(): {
  balances: Record<string, TokenBalance>;
  ethBalance: { raw: bigint; formatted: string } | null;
  loading: boolean;
  connectionError: boolean;
  errors: BalanceFetchError[];
  refetch: () => void;
} {
  const { publicClient, address, deployment } = useEvmBackend();

  const [balances, setBalances] = useState<Record<string, TokenBalance>>({});
  const [ethBalance, setEthBalance] = useState<{
    raw: bigint;
    formatted: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [errors, setErrors] = useState<BalanceFetchError[]>([]);
  const fetchingRef = useRef(false);
  const initialLoadDone = useRef(false);

  const refetch = useCallback(async () => {
    if (!address) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    // Only show loading spinner on initial fetch — subsequent refetches keep stale data visible
    if (!initialLoadDone.current) {
      setLoading(true);
    }

    const newErrors: BalanceFetchError[] = [];
    let connError = false;

    try {
      const eth = await publicClient.getBalance({ address });
      setEthBalance({ raw: eth, formatted: formatUnits(eth, 18) });
    } catch (err) {
      const isConn = isConnectionError(err);
      connError = isConn;
      newErrors.push({
        token: "ETH",
        message: parseViemError(err),
        isConnection: isConn,
      });
      // Keep previous ethBalance on error instead of clearing
    }

    if (deployment?.tokens) {
      try {
        const tokens = deployment.tokens;
        const allSymbols = Object.keys(tokens) as (keyof typeof tokens)[];

        // Filter out tokens with invalid addresses (e.g. "0x" placeholders from fork mode)
        const validSymbols = allSymbols.filter((symbol) =>
          isValidTokenAddress(tokens[symbol]),
        );

        const results = await Promise.allSettled(
          validSymbols.map((symbol) =>
            publicClient.readContract({
              address: tokens[symbol],
              abi: MOCK_ERC20_ABI,
              functionName: "balanceOf",
              args: [address],
            }),
          ),
        );

        const newBalances: Record<string, TokenBalance> = {};
        validSymbols.forEach((symbol, i) => {
          const result = results[i];
          if (!result) return;
          const upperSymbol = symbol.toUpperCase();
          if (result.status === "fulfilled") {
            const raw = result.value;
            const decimals = TOKEN_DECIMALS[upperSymbol] ?? 18;
            newBalances[upperSymbol] = {
              raw,
              formatted: formatUnits(raw, decimals),
              decimals,
            };
          } else {
            newErrors.push({
              token: upperSymbol,
              message: parseViemError(result.reason),
              isConnection: false,
            });
          }
        });

        setBalances(newBalances);
      } catch (err) {
        const isConn = isConnectionError(err);
        if (isConn) connError = true;
        newErrors.push({
          token: "ERC20",
          message: parseViemError(err),
          isConnection: isConn,
        });
        // Keep previous balances on error instead of clearing
      }
    } else if (!initialLoadDone.current) {
      setBalances({});
    }

    setConnectionError(connError);
    setErrors(newErrors);
    setLoading(false);
    initialLoadDone.current = true;
    fetchingRef.current = false;
  }, [address, deployment?.tokens, publicClient]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useOnBlock(() => {
    void refetch();
  });

  const refetchSync = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    balances,
    ethBalance,
    loading,
    connectionError,
    errors,
    refetch: refetchSync,
  };
}
